import React, {useEffect, useState} from "react";
import {Container} from "@mui/material";

import Navigation from "../../components/Navigation";
import Header from "./Header/Header";

import {StickyContent} from "./StickyContent/StickyContent";
import {Statistics} from "./Statistics/Statistics";
import {EconomicSection} from "./Charts/EconomicSection/EconomicSection";
import {EmploymentSection} from "./Charts/EmploymentSection/EmploymentSection";
import {SummaryPage} from "./SummaryPage/SummaryPage";
import Footer from "../../components/Footer";
import {LinearGradient} from "../../components/Icons/LinearGradient";
import {fetchCountriesPerUserDataAsync} from "../../features/countries/countriesPerUserSlice";
import {useDispatch, useSelector} from "react-redux";
import {AppDispatch, RootState} from "../../store/store";
import {fetchMapDataAsync} from "../../features/map/mapApiSlice";
import {BarController, BarElement, CategoryScale, Chart, Legend, LinearScale, Title, Tooltip,} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {setCaricomCountry, setDefaultCountry, setSelectedCountry,} from "../../features/map/mapSlice";
import {ImpactOverTimeSection} from "./Charts/ImpactOverTime/ImpactOverTimeSection";
import {isPDFGenerationEnabled, isRegion, regionEnv} from "../../config";
import {setCurrentRegion} from "../../features/regions/regionsSlice";
import {fetchStatisticsDataAsync, setStatisticsForSelectedCountry,} from "../../features/statistics/statisticsSlice";
import {StatisticsState} from "../../features/statistics/types";
import {fetchPinsDataAsync} from "../../features/map/pinsSlice";
import {LATAM_COUNTRY_NAMES} from "../../features/countries/types";
import {fetchAllCountriesDataAsync} from "../../features/countries/countriesAllSlice";

Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Legend,
  ChartDataLabels,
  Tooltip
);

const MainPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    map: {selectedCountry, year},
    countriesPerUser: {data: countriesData},
    regions: {data: regionsData},
    statistics: {data: statisticsDataForAllCountries},
  } = useSelector((state: RootState) => state);
  const ref = React.useRef<HTMLDivElement>(null);

  const [
    fittedStatisticsForSelectedCountry,
    setFittedStatisticsForSelectedCountry,
  ] = useState<StatisticsState["statisticsForSelectedCountry"]>(null);

  useEffect(() => {
    dispatch(fetchCountriesPerUserDataAsync(year));
    dispatch(fetchMapDataAsync("2021"));
  }, [dispatch, year]);

  useEffect(() => {
    if(isPDFGenerationEnabled){
      dispatch(fetchAllCountriesDataAsync());
    }
  }, []);

  const defaultCountry = countriesData.find(
    (country) => country.country_name.toUpperCase() === regionEnv
  );
  const currentRegion = regionsData.find(
    (region) => region.name.toUpperCase() === regionEnv
  );

  useEffect(() => {
    dispatch(setDefaultCountry(defaultCountry));
    dispatch(setCurrentRegion(currentRegion));

    if (!selectedCountry) {
      dispatch(setSelectedCountry(defaultCountry));
    }
  }, [currentRegion, dispatch, defaultCountry, selectedCountry]);

  useEffect(() => {
    dispatch(fetchStatisticsDataAsync(year));
  }, [dispatch, year]);

  useEffect(() => {
    const fittedStatisticsForCountry = statisticsDataForAllCountries?.find(
      (statisticsForCountry) =>
        statisticsForCountry?.country_name?.toUpperCase() === selectedCountry?.country_name?.toUpperCase()
    );
    fittedStatisticsForCountry &&
    setFittedStatisticsForSelectedCountry(fittedStatisticsForCountry);
  }, [statisticsDataForAllCountries, selectedCountry]);

  useEffect(() => {
    fittedStatisticsForSelectedCountry &&
    dispatch(
      setStatisticsForSelectedCountry(fittedStatisticsForSelectedCountry)
    );
  }, [dispatch, fittedStatisticsForSelectedCountry]);

  useEffect(() => {
    dispatch(fetchPinsDataAsync("2021"));
  }, [dispatch, year]);

  useEffect(() => {
    const caricomCountry = countriesData.find(
      (country) => country.country_name.toUpperCase() === LATAM_COUNTRY_NAMES.CARICOM.toUpperCase()
    );

    if (caricomCountry) {
      dispatch(setCaricomCountry(caricomCountry));
    }
  }, [countriesData, dispatch]);

  return (
    <div id="Page-id" ref={ref}
         style={{
           display: "flex",
           flexDirection: "column"
         }}
    >
      <LinearGradient
        width="100%"
        height="400px"
        style={{
          position: "absolute",
          zIndex: -1,
          transform: "translateY(800px)",
        }}
      />
      <Navigation/>
      <Container maxWidth={"xl"}>
        <Header/>
      </Container>
      <StickyContent/>
      <Container maxWidth={"xl"}>
        <Statistics printRef={ref}/>
        <EconomicSection/>
        <EmploymentSection/>
        {isRegion.romania && <ImpactOverTimeSection/>}
        <SummaryPage/>
      </Container>
      <Footer/>
    </div>
  );
};
export default MainPage;
